import { apiClient } from '../../../client'

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface UserLeaveAttachment {
  id?: number
  leave_id?: number
  file_name: string
  file_type?: string | null
  display_name?: string | null
  file_url?: string | null
  created_at?: string
}

export interface UserLeave {
  id: number
  tenant_id: number
  user_id: number
  from_date: string
  to_date: string
  leave_type: string
  status: string
  reason?: string | null
  approved_by?: number | null
  approved_at?: string | null
  rejected_by?: number | null
  rejected_at?: string | null
  created_by?: number | null
  updated_by?: number | null
  created_at?: string
  updated_at?: string
  user_name?: string
  user_username?: string
  attachment_count?: number
  attachments?: UserLeaveAttachment[]
}

export interface UserLeaveFilters {
  search?: string
  status?: string
  leave_type?: string
}

export interface CreateUserLeaveRequest {
  user_id: number
  from_date: string
  to_date: string
  leave_type: string
  status?: string
  reason?: string | null
  attachments?: UserLeaveAttachment[]
}

export type UpdateUserLeaveRequest = CreateUserLeaveRequest

class UserLeaveRepository {
  async findAll(
    page: number = 1,
    limit: number = 10,
    filters?: UserLeaveFilters,
  ): Promise<PaginatedResponse<UserLeave>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    })
    if (filters?.search) params.append('search', filters.search)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.leave_type) params.append('leave_type', filters.leave_type)
    return apiClient.get<PaginatedResponse<UserLeave>>(`/field-force/user-leaves?${params.toString()}`)
  }

  async getById(id: number): Promise<UserLeave> {
    return apiClient.get<UserLeave>(`/field-force/user-leaves/${id}`)
  }

  async create(data: CreateUserLeaveRequest): Promise<UserLeave> {
    return apiClient.post<UserLeave>('/field-force/user-leaves', data)
  }

  async update(id: number, data: UpdateUserLeaveRequest): Promise<UserLeave> {
    return apiClient.put<UserLeave>(`/field-force/user-leaves/${id}`, data)
  }

  async delete(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/field-force/user-leaves/${id}`)
  }

  async addAttachments(
    leaveId: number,
    attachments: UserLeaveAttachment[],
  ): Promise<UserLeaveAttachment[]> {
    return apiClient.post<UserLeaveAttachment[]>(`/field-force/user-leaves/${leaveId}/attachments`, {
      attachments,
    })
  }

  async deleteAttachment(leaveId: number, attachmentId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `/field-force/user-leaves/${leaveId}/attachments/${attachmentId}`,
    )
  }
}

export const userLeaveRepository = new UserLeaveRepository()
